var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isMobile } from '@/core/platform';
import { getApiAddress, getApiColorwaysEndpoint, getApiCustomizerColorwaySwitchEndpoint, getApiCustomizerRecipesEndpoint, getApiCustomizerSceneEndpoint, getApiFullStyleEndpoint, getApiLogosForGeneratorsEndpoint, getApiSkipAuth, } from './apiConfig';
export class StyleNotFoundError extends Error {
    constructor(message) {
        super(message);
        this.name = 'StyleNotFoundError';
    }
}
/** A recipe request the user can correct, such as a rejected URL or invalid recipe file. */
export class StyleLoadError extends Error {
    constructor(message) {
        super(message);
        this.name = 'StyleLoadError';
    }
}
const FULL_STYLE_ENDPOINT = getApiFullStyleEndpoint();
const SCENE_QUALITY = isMobile() ? 'LOW' : 'MEDIUM';
function buildHeaders(apiKey) {
    return getApiSkipAuth() ? {} : { 'content-type': 'application/json', 'x-api-key': apiKey };
}
export function fetchStyleAggregatorData(params) {
    return __awaiter(this, void 0, void 0, function* () {
        const { apiKey, signal } = params;
        const search = new URLSearchParams({ quality: SCENE_QUALITY });
        if (params.mockupId) {
            search.set('mockup_id', params.mockupId);
        }
        else {
            const { brand, styleNumber, colorway } = params;
            if (!brand || !styleNumber) {
                throw new Error('Missing required parameters "brand" and/or "styleNumber" for style lookup.');
            }
            search.set('brand', brand);
            search.set('number', styleNumber);
            if (colorway)
                search.set('colorway', colorway);
        }
        const url = `${getApiAddress()}/${FULL_STYLE_ENDPOINT}?${search.toString()}`;
        const response = yield fetch(url, {
            headers: buildHeaders(apiKey),
            signal,
        });
        if (!response.ok) {
            if (response.status === 404) {
                if (params.mockupId) {
                    throw new StyleNotFoundError(`No styles found for Mockup ${params.mockupId}`);
                }
                const { brand, styleNumber, colorway } = params;
                throw new StyleNotFoundError(`No styles found for Brand ${brand}, Style Number ${styleNumber}, Colorway ${colorway !== null && colorway !== void 0 ? colorway : 'ANY'}`);
            }
            throw new Error(`Failed to fetch full style data: ${response.status}`);
        }
        return response.json();
    });
}
function toMockupLogo(logo) {
    return {
        id: logo.id,
        name: logo.name,
        displayName: logo.displayName,
        imageSrc: logo.imageSrc,
        imageSize: logo.imageSize,
        region: logo.region,
        size: logo.size,
        sourceUrl: logo.sourceUrl,
        transform: logo.transform,
        technique: logo.technique,
        customTechnique: logo.customTechnique,
    };
}
function fileNameFromUrl(sourceUrl) {
    try {
        const segment = new URL(sourceUrl).pathname.split('/').filter(Boolean).pop();
        return segment ? decodeURIComponent(segment) : '';
    }
    catch (_a) {
        return '';
    }
}
function withoutExtension(name) {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(0, dot) : name;
}
function mapProcessedLogo(entry, sourceUrl) {
    var _a;
    const processed = entry === null || entry === void 0 ? void 0 : entry.processed;
    const imageSrc = processed === null || processed === void 0 ? void 0 : processed.signedUrl;
    const width = processed === null || processed === void 0 ? void 0 : processed.width;
    const height = processed === null || processed === void 0 ? void 0 : processed.height;
    if (typeof imageSrc !== 'string' ||
        !imageSrc.trim() ||
        typeof width !== 'number' ||
        !Number.isFinite(width) ||
        width <= 0 ||
        typeof height !== 'number' ||
        !Number.isFinite(height) ||
        height <= 0) {
        throw new StyleLoadError('logos-for-generators returned an unexpected response shape');
    }
    const fileName = (typeof entry.name === 'string' && entry.name.trim()) || fileNameFromUrl(sourceUrl);
    const name = fileName || 'logo';
    const displayName = withoutExtension(name) || 'Logo';
    const originalSignedUrl = typeof ((_a = entry.original) === null || _a === void 0 ? void 0 : _a.signedUrl) === 'string' ? entry.original.signedUrl : undefined;
    return {
        sourceUrl,
        name,
        displayName,
        imageSrc,
        imageSize: { x: width, y: height },
        originalSignedUrl,
    };
}
/**
 * Store a finalized recipe via customizer-recipes and return a short-lived signed URL.
 */
export function saveCustomizerRecipe(params) {
    return __awaiter(this, void 0, void 0, function* () {
        const url = `${getApiAddress()}/${getApiCustomizerRecipesEndpoint()}`;
        const response = yield fetch(url, {
            method: 'POST',
            headers: buildHeaders(params.apiKey),
            body: JSON.stringify(params.recipe),
            signal: params.signal,
        });
        if (!response.ok) {
            const message = yield readErrorMessage(response, `Failed to save recipe: ${response.status}`);
            if (response.status === 400) {
                throw new StyleLoadError(message);
            }
            throw new Error(message);
        }
        const data = (yield response.json());
        if (typeof data.recipeUrl !== 'string' ||
            !data.recipeUrl.trim() ||
            typeof data.expiresAt !== 'string' ||
            !data.expiresAt.trim()) {
            throw new StyleLoadError('customizer-recipes returned an unexpected response shape');
        }
        return {
            recipeUrl: data.recipeUrl,
            expiresAt: data.expiresAt,
        };
    });
}
/**
 * Process a single HTTPS logo URL via logos-for-generators (through the API gateway).
 */
export function fetchProcessedLogo(params) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const sourceUrl = params.sourceUrl.trim();
        const url = `${getApiAddress()}/${getApiLogosForGeneratorsEndpoint()}`;
        const response = yield fetch(url, {
            method: 'POST',
            headers: buildHeaders(params.apiKey),
            body: JSON.stringify({
                sourceUrls: [sourceUrl],
                options: {
                    extractColorPalette: false,
                    callerService: 'customizer',
                },
            }),
            signal: params.signal,
        });
        if (!response.ok) {
            const message = yield readErrorMessage(response, `Failed to process logo: ${response.status}`);
            if (response.status === 400) {
                throw new StyleLoadError(message);
            }
            throw new Error(message);
        }
        const data = (yield response.json());
        const entry = (_a = data.logos) === null || _a === void 0 ? void 0 : _a[0];
        if (!entry) {
            throw new StyleLoadError('logos-for-generators returned no logos');
        }
        return mapProcessedLogo(entry, sourceUrl);
    });
}
function readErrorMessage(response, fallback) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const body = (yield response.json());
            if (typeof (body === null || body === void 0 ? void 0 : body.error) === 'string' && body.error.trim()) {
                return body.error;
            }
        }
        catch (_a) {
            // The body is not JSON; use the fallback.
        }
        return fallback;
    });
}
/**
 * Load a style from a recipe file URL via customizer-scene-generator.
 * The returned scene is the filtered base scene. Recipe logos stay in `logos` so the viewer can edit them.
 */
export function fetchCustomizerScene(params) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        const url = `${getApiAddress()}/${getApiCustomizerSceneEndpoint()}`;
        const response = yield fetch(url, {
            method: 'POST',
            headers: buildHeaders(params.apiKey),
            body: JSON.stringify({ quality: SCENE_QUALITY, recipeUrl: params.recipeUrl }),
            signal: params.signal,
        });
        if (!response.ok) {
            const message = yield readErrorMessage(response, `Failed to fetch customizer scene: ${response.status}`);
            if (response.status === 404) {
                throw new StyleNotFoundError(message);
            }
            if (response.status === 400) {
                throw new StyleLoadError(message);
            }
            throw new Error(message);
        }
        const data = (yield response.json());
        return {
            brand: data.brand,
            catalogStyle: (_a = data.catalogStyle) !== null && _a !== void 0 ? _a : undefined,
            colorway: data.colorway,
            hideTearAwayLabel: Boolean(data.hideTearAwayLabel),
            isBrandApproved: Boolean(data.isBrandApproved),
            logos: ((_b = data.logos) !== null && _b !== void 0 ? _b : []).map(toMockupLogo),
            pointsOfMeasure: (_c = data.pointsOfMeasure) !== null && _c !== void 0 ? _c : [],
            recipeSource: data.recipeSource,
            scene: data.scene,
            sessionToken: typeof data.sessionToken === 'string' ? data.sessionToken : undefined,
            sessionTokenExpiresAt: typeof data.sessionTokenExpiresAt === 'string' ? data.sessionTokenExpiresAt : undefined,
            sizes: (_d = data.sizes) !== null && _d !== void 0 ? _d : [],
            style: data.style,
            styleId: data.styleId,
            styleNumber: data.styleNumber,
            styleRegions: (_e = data.styleRegions) !== null && _e !== void 0 ? _e : [],
        };
    });
}
const COLORWAY_PAGE_SIZE = 100;
const COLORWAY_PAGE_LIMIT = 20;
/**
 * All published color names for a brand and style number.
 * Pages until a short page comes back. Discovery does not filter by size.
 */
export function fetchDiscoveredColorways(params) {
    return __awaiter(this, void 0, void 0, function* () {
        const colors = [];
        const seen = new Set();
        for (let page = 1; page <= COLORWAY_PAGE_LIMIT; page += 1) {
            const search = new URLSearchParams({
                brand: params.brand,
                number: params.styleNumber,
                page: String(page),
                page_size: String(COLORWAY_PAGE_SIZE),
            });
            const url = `${getApiAddress()}/${getApiColorwaysEndpoint()}?${search.toString()}`;
            const response = yield fetch(url, {
                headers: buildHeaders(params.apiKey),
                signal: params.signal,
            });
            if (!response.ok) {
                const message = yield readErrorMessage(response, `Failed to list colorways: ${response.status}`);
                throw new StyleLoadError(message);
            }
            const body = (yield response.json());
            const batch = Array.isArray(body.data) ? body.data : [];
            let added = 0;
            for (const row of batch) {
                const color = typeof (row === null || row === void 0 ? void 0 : row.color) === 'string' ? row.color.trim() : '';
                if (!color || seen.has(color))
                    continue;
                seen.add(color);
                colors.push(color);
                added += 1;
            }
            if (batch.length < COLORWAY_PAGE_SIZE || added === 0)
                break;
        }
        colors.sort((a, b) => a.localeCompare(b));
        return colors;
    });
}
/**
 * Load another colorway of the garment locked by a scene-generator session token.
 */
export function switchCustomizerColorway(params) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const url = `${getApiAddress()}/${getApiCustomizerColorwaySwitchEndpoint()}`;
        const response = yield fetch(url, {
            method: 'POST',
            headers: buildHeaders(params.apiKey),
            body: JSON.stringify({ sessionToken: params.sessionToken, color: params.color }),
            signal: params.signal,
        });
        if (!response.ok) {
            const message = yield readErrorMessage(response, `Failed to switch colorway: ${response.status}`);
            if (response.status === 400 || response.status === 401 || response.status === 404) {
                throw new StyleLoadError(message);
            }
            throw new Error(message);
        }
        const data = (yield response.json());
        return {
            brand: data.brand,
            catalogStyle: (_a = data.catalogStyle) !== null && _a !== void 0 ? _a : undefined,
            colorway: data.colorway,
            isBrandApproved: Boolean(data.isBrandApproved),
            pointsOfMeasure: (_b = data.pointsOfMeasure) !== null && _b !== void 0 ? _b : [],
            scene: data.scene,
            sizes: (_c = data.sizes) !== null && _c !== void 0 ? _c : [],
            style: data.style,
            styleId: data.styleId,
            styleNumber: data.styleNumber,
            styleRegions: (_d = data.styleRegions) !== null && _d !== void 0 ? _d : [],
        };
    });
}
